import React from "react";
import { isDompeurTruck, truckTypeLabel } from "../utils/dataManagerFleetUnits.js";
import { retirementNotes } from "../utils/dataManagerFleetRetirement.js";

const TOGGLE_CLASS_NAME = "inline-flex h-7 items-center gap-1.5 rounded-[4px] border px-3 text-xs font-semibold focus-visible:outline-accent disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400";
const TOGGLE_ON_CLASS_NAME = "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong";
const TOGGLE_OFF_CLASS_NAME = "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900";

function Toggles({ label, options, value, onChange, disabled = false, ...props }) {
  return <div role="group" aria-label={label} className="flex gap-1" {...props}>
    {options.map(([key, text, count]) => <button key={key} type="button" aria-pressed={value === key} disabled={disabled}
      onClick={() => onChange(key)} className={`${TOGGLE_CLASS_NAME} ${value === key ? TOGGLE_ON_CLASS_NAME : TOGGLE_OFF_CLASS_NAME}`}>
      {text}
      {count === undefined ? null : <span className="tabular-nums opacity-80">{count}</span>}
    </button>)}
  </div>;
}

// « Dans la flotte » / « Retirés » : par partie (le camion dans Camions, la remorque dans Remorques).
export function DataManagerFleetFilter({ value = "fleet", counts = { fleet: 0, retired: 0 }, onChange, disabled = false, retiredLabel = "Retirés" }) {
  return <Toggles label="Flotte" data-fleet-filter value={value} onChange={onChange} disabled={disabled}
    options={[["fleet", "Dans la flotte", counts.fleet ?? 0], ["retired", retiredLabel, counts.retired ?? 0]]} />;
}

// Dates de retrait sous la valeur : le type dans Camions, le nom de la remorque dans Remorques.
export function DataManagerRetirementNotes({ truck, children }) {
  const notes = retirementNotes(truck);
  if (!notes.length) return children;
  return <span className="inline-flex flex-col items-start gap-0.5">
    {children}
    {notes.map((note) => <span key={note} data-truck-retirement className="whitespace-nowrap text-[10px] font-medium text-slate-500">{note}</span>)}
  </span>;
}

export function DataManagerTypeBadge({ truck }) {
  return <DataManagerRetirementNotes truck={truck}>
    <span data-truck-type className="inline-flex items-center whitespace-nowrap rounded-[4px] border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-600">
      {truckTypeLabel(truck)}
    </span>
  </DataManagerRetirementNotes>;
}

// « Ajouter un camion » : une citerne (un camion sans remorque, il tire une remorque existante) ou un dompeur (d'une seule pièce).
export function DataManagerNewTruckKind({ draft, onChange, disabled = false }) {
  return <div className="flex items-center gap-2">
    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Nouveau</span>
    <Toggles label="Type du nouveau camion" value={isDompeurTruck(draft) ? "dompeur" : "camion"} onChange={onChange} disabled={disabled}
      options={[["camion", "Citerne"], ["dompeur", "Dompeur"]]} />
  </div>;
}

const NEW_UNIT_NOTES = {
  camion: ["Citerne", "un camion sans remorque ; il tire une remorque de l'onglet Remorques, choisie au Gantt (« Changer de camion », où il est « Libre »)."],
  dompeur: ["Dompeur", "d'une seule pièce, il n'est jamais attelé, sans aspirateur ni produit associé. Sa capacité, son pool preload et son rayon Soir se règlent sur sa ligne, dans cet onglet, dès l'ajout."],
  remorque: ["Remorque seule", "sans camion ni chauffeur, elle reste « Camion à choisir » jusqu'à ce qu'un camion la tire : au Gantt, « Ajouter » › « Une remorque », puis « Changer de camion ». Les compartiments s'enregistrent avec elle ; un compartiment refusé est signalé, la remorque reste créée."],
};

export function DataManagerNewUnitNote({ kind }) {
  const [title, text] = NEW_UNIT_NOTES[kind] || NEW_UNIT_NOTES.camion;
  return <p data-new-unit-note className="mt-2 mb-0 rounded-[4px] border border-slate-200 bg-white px-3 py-2 text-[11px] leading-4 text-slate-600">
    <strong className="font-semibold text-slate-800">{title}</strong> : {text}
  </p>;
}
